import Ajv, { type ErrorObject } from 'ajv';
import addFormats from 'ajv-formats';
import type {
  Action,
  BuildingMinigame,
  Condition,
  GameDefinition,
  ItemGenerator,
  MathChallenge,
  OrderingMinigame,
  SortingMinigame
} from '../types/game';
import { generateItems, isNumericGenerator } from '../minigames/items';
import gameSchema from './game.schema.json';

/** Tope de piezas para la búsqueda de combinaciones alternativas en «construir». */
const MAX_SUBSET_PIECES = 20;

/** Problema de validación con su ubicación dentro del JSON. */
export interface ValidationIssue {
  /** Ruta JSON (JSON Pointer simplificado) donde está el problema. */
  path: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  /** Errores de estructura según el JSON Schema. */
  schemaErrors: ValidationIssue[];
  /** Errores de referencias entre entidades (ID inexistente, recurso faltante…). */
  referenceErrors: ValidationIssue[];
}

let ajvInstance: Ajv | null = null;
function getAjv(): Ajv {
  if (!ajvInstance) {
    const ajv = new Ajv({ allErrors: true, strict: false });
    addFormats(ajv);
    ajv.addSchema(gameSchema, 'game');
    ajvInstance = ajv;
  }
  return ajvInstance;
}

function ajvPath(err: ErrorObject): string {
  return err.instancePath || '(raíz)';
}

/** Valida únicamente contra el JSON Schema. */
export function validateSchema(data: unknown): ValidationIssue[] {
  const ajv = getAjv();
  const validate = ajv.getSchema('game')!;
  const ok = validate(data);
  if (ok) return [];
  return (validate.errors ?? []).map((e) => ({
    path: ajvPath(e),
    message: `${e.message ?? 'error'}${
      e.params && Object.keys(e.params).length ? ' ' + JSON.stringify(e.params) : ''
    }`.trim()
  }));
}

/* ------------------------------------------------------------------ */
/* Validación de referencias entre entidades                            */
/* ------------------------------------------------------------------ */

class RefChecker {
  readonly issues: ValidationIssue[] = [];
  private readonly images: Set<string>;
  private readonly audio: Set<string>;
  private readonly scenes: Set<string>;
  private readonly characters: Set<string>;
  private readonly dialogues: Set<string>;
  private readonly items: Set<string>;
  private readonly evidence: Set<string>;
  private readonly objectives: Set<string>;
  private readonly endings: Set<string>;
  private readonly hypotheses: Set<string>;
  private readonly measures: Set<string>;
  private readonly minigames: Set<string>;
  private readonly battles: Set<string>;

  constructor(private readonly game: GameDefinition) {
    this.images = new Set(Object.keys(game.assets.images ?? {}));
    this.audio = new Set(Object.keys(game.assets.audio ?? {}));
    this.scenes = new Set(Object.keys(game.scenes));
    this.characters = new Set(Object.keys(game.characters));
    this.dialogues = new Set(Object.keys(game.dialogues));
    this.items = new Set(Object.keys(game.items));
    this.evidence = new Set(Object.keys(game.evidence));
    this.objectives = new Set(Object.keys(game.objectives));
    this.endings = new Set(Object.keys(game.endings));
    this.hypotheses = new Set((game.conclusion?.hypotheses ?? []).map((h) => h.id));
    this.measures = new Set((game.conclusion?.measures ?? []).map((m) => m.id));
    this.minigames = new Set(Object.keys(game.minigames ?? {}));
    this.battles = new Set(Object.keys(game.battles ?? {}));
  }

  private fail(path: string, message: string): void {
    this.issues.push({ path, message });
  }

  private checkRef(
    path: string,
    id: string | undefined,
    set: Set<string>,
    label: string
  ): void {
    if (id === undefined) return;
    if (!set.has(id)) this.fail(path, `${label} inexistente: "${id}"`);
  }

  checkCondition(cond: Condition | undefined, path: string): void {
    if (!cond) return;
    if ('all' in cond) {
      cond.all.forEach((c, i) => this.checkCondition(c, `${path}/all/${i}`));
      return;
    }
    if ('any' in cond) {
      cond.any.forEach((c, i) => this.checkCondition(c, `${path}/any/${i}`));
      return;
    }
    if ('not' in cond) {
      this.checkCondition(cond.not, `${path}/not`);
      return;
    }
    if ('hasItem' in cond)
      return this.checkRef(`${path}/hasItem`, cond.hasItem, this.items, 'Item');
    if ('hasEvidence' in cond)
      return this.checkRef(`${path}/hasEvidence`, cond.hasEvidence, this.evidence, 'Evidencia');
    if ('visitedScene' in cond)
      return this.checkRef(`${path}/visitedScene`, cond.visitedScene, this.scenes, 'Escenario');
    if ('objectiveCompleted' in cond)
      return this.checkRef(
        `${path}/objectiveCompleted`,
        cond.objectiveCompleted,
        this.objectives,
        'Objetivo'
      );
    if ('hypothesisIs' in cond)
      return this.checkRef(`${path}/hypothesisIs`, cond.hypothesisIs, this.hypotheses, 'Hipótesis');
    if ('evidenceSelected' in cond)
      return this.checkRef(
        `${path}/evidenceSelected`,
        cond.evidenceSelected,
        this.evidence,
        'Evidencia'
      );
    if ('measureSelected' in cond)
      return this.checkRef(
        `${path}/measureSelected`,
        cond.measureSelected,
        this.measures,
        'Medida'
      );
    // var / always / selectedEvidenceCount / selectedMeasuresCount no referencian IDs.
  }

  checkActions(actions: Action[] | undefined, path: string, scene?: { objects?: Record<string, import('../types/game').SceneObject>; sequences?: Record<string, import('../types/game').Sequence> }): void {
    if (!actions) return;
    actions.forEach((action, i) => {
      const p = `${path}/${i}`;
      switch (action.type) {
        case 'addItem':
        case 'removeItem':
          this.checkRef(`${p}/item`, action.item, this.items, 'Item');
          break;
        case 'addEvidence':
          this.checkRef(`${p}/evidence`, action.evidence, this.evidence, 'Evidencia');
          break;
        case 'goToScene':
          this.checkRef(`${p}/scene`, action.scene, this.scenes, 'Escenario');
          break;
        case 'startDialogue':
          this.checkRef(`${p}/dialogue`, action.dialogue, this.dialogues, 'Diálogo');
          if (action.node) {
            const d = this.game.dialogues[action.dialogue];
            if (d && !(action.node in d.nodes))
              this.fail(`${p}/node`, `Nodo de diálogo inexistente: "${action.node}"`);
          }
          break;
        case 'completeObjective':
          this.checkRef(`${p}/objective`, action.objective, this.objectives, 'Objetivo');
          break;
        case 'finishGame':
          this.checkRef(`${p}/ending`, action.ending, this.endings, 'Desenlace');
          break;
        case 'playSequence': {
          const seqs = scene?.sequences;
          if (seqs && !(action.sequence in seqs))
            this.fail(`${p}/sequence`, `Secuencia inexistente en la escena: "${action.sequence}"`);
          break;
        }
        case 'setObjectState': {
          const objs = scene?.objects;
          if (objs) {
            if (!(action.object in objs))
              this.fail(`${p}/object`, `Objeto inexistente en la escena: "${action.object}"`);
            else {
              const obj = objs[action.object];
              if (!(action.state in obj.states))
                this.fail(`${p}/state`, `Estado inexistente en objeto "${action.object}": "${action.state}"`);
            }
          }
          break;
        }
        case 'playVideo': {
          if (action.modo === 'tv') {
            if (!action.objeto) {
              this.fail(`${p}/objeto`, 'El modo "tv" necesita el objeto del mapa que hace de pantalla');
            } else if (scene?.objects && !(action.objeto in scene.objects)) {
              this.fail(`${p}/objeto`, `Objeto inexistente en el mapa: "${action.objeto}"`);
            }
          }
          if (action.poster) this.checkAsset(`${p}/poster`, action.poster, 'image');
          break;
        }
        case 'startEncounter': {
          this.checkRef(`${p}/encounter`, action.encounter, this.battles, 'Batalla');
          break;
        }
        case 'startMinigame': {
          this.checkRef(`${p}/minigame`, action.minigame, this.minigames, 'Minijuego');
          break;
        }
        default:
          break;
      }
    });
  }

  private checkAsset(path: string, ref: string | undefined, kind: 'image' | 'audio'): void {
    if (ref === undefined) return;
    const set = kind === 'image' ? this.images : this.audio;
    if (!set.has(ref))
      this.fail(path, `Recurso ${kind} no declarado en assets: "${ref}"`);
  }

  /**
   * Catálogo de minijuegos.
   *
   * Estas invariantes son las que sostienen la promesa de la colección: un reto
   * declarado aquí siempre es resoluble y nunca tiene dos respuestas válidas.
   * Sin ellas, un error de autoría se vería recién en el aula.
   */
  private checkMinigames(): void {
    for (const [mid, spec] of Object.entries(this.game.minigames ?? {})) {
      const p = `minigames/${mid}`;
      if (spec.id !== mid) this.fail(`${p}/id`, `El id "${spec.id}" no coincide con la clave "${mid}"`);
      this.checkActions(spec.onSuccess, `${p}/onSuccess`);
      this.checkActions(spec.onFailure, `${p}/onFailure`);

      switch (spec.kind) {
        case 'opcion-multiple': {
          if (spec.generador) {
            this.checkGenerator(`${p}/generador`, spec.generador, spec.cantidad ?? 1, spec.semilla ?? 1);
          } else {
            const opciones = spec.opciones ?? [];
            if ((spec.correcta ?? 0) >= opciones.length)
              this.fail(`${p}/correcta`, `Índice fuera de rango: hay ${opciones.length} opciones`);
            this.checkSinDuplicados(`${p}/opciones`, opciones, 'opción');
          }
          break;
        }
        case 'respuesta-numerica': {
          if (spec.generador) {
            if (!isNumericGenerator(spec.generador))
              this.fail(`${p}/generador`, `El generador "${spec.generador.tipo}" no produce un número; usa «opcion-multiple»`);
            else this.checkGenerator(`${p}/generador`, spec.generador, spec.cantidad ?? 1, spec.semilla ?? 1);
          }
          break;
        }
        case 'ordenar':
        case 'recta-numerica':
          this.checkOrdering(p, spec);
          break;
        case 'emparejar':
          this.checkSinDuplicados(`${p}/pares`, spec.pares.map((par) => par.izquierda), 'elemento izquierdo');
          this.checkSinDuplicados(`${p}/pares`, spec.pares.map((par) => par.derecha), 'elemento derecho');
          break;
        case 'clasificar':
          this.checkSorting(p, spec);
          break;
        case 'construir':
          this.checkBuilding(p, spec);
          break;
        case 'rafaga':
          this.checkGenerator(`${p}/generador`, spec.generador, spec.cantidad, spec.semilla ?? 1);
          break;
      }
    }
  }

  /** El rango declarado tiene que alcanzar para generar los ítems pedidos. */
  private checkGenerator(path: string, generador: ItemGenerator, cantidad: number, semilla: number): void {
    const items = generateItems(generador, cantidad, semilla);
    if (items.length < cantidad)
      this.fail(path, `El rango sólo produce ${items.length} ítem(s) distinto(s); se pidieron ${cantidad}`);
  }

  private checkSinDuplicados(path: string, values: string[], label: string): void {
    const vistos = new Set<string>();
    for (const value of values) {
      if (vistos.has(value)) this.fail(path, `${label} repetido: "${value}" (dejaría dos respuestas válidas)`);
      vistos.add(value);
    }
  }

  private checkIdsUnicos(path: string, ids: string[]): void {
    this.checkSinDuplicados(path, ids, 'id de pieza');
  }

  private checkOrdering(path: string, spec: OrderingMinigame): void {
    const ids = spec.piezas.map((pz) => pz.id);
    this.checkIdsUnicos(`${path}/piezas`, ids);
    this.checkSinDuplicados(`${path}/orden`, spec.orden, 'pieza');
    const ajenas = spec.orden.filter((id) => !ids.includes(id));
    if (ajenas.length > 0) this.fail(`${path}/orden`, `piezas inexistentes: ${ajenas.join(', ')}`);
    const omitidas = ids.filter((id) => !spec.orden.includes(id));
    if (omitidas.length > 0) this.fail(`${path}/orden`, `faltan piezas por ordenar: ${omitidas.join(', ')}`);
  }

  private checkSorting(path: string, spec: SortingMinigame): void {
    this.checkIdsUnicos(`${path}/piezas`, spec.piezas.map((pz) => pz.id));
    this.checkIdsUnicos(`${path}/cestas`, spec.cestas.map((c) => c.id));
    const cestas = new Set(spec.cestas.map((c) => c.id));
    const usadas = new Set<string>();
    for (const pieza of spec.piezas) {
      if (!pieza.bucket) {
        this.fail(`${path}/piezas`, `la pieza "${pieza.id}" no declara cesta`);
        continue;
      }
      if (!cestas.has(pieza.bucket))
        this.fail(`${path}/piezas`, `la pieza "${pieza.id}" apunta a una cesta inexistente: "${pieza.bucket}"`);
      usadas.add(pieza.bucket);
    }
    for (const cesta of spec.cestas)
      if (!usadas.has(cesta.id)) this.fail(`${path}/cestas`, `la cesta "${cesta.id}" queda vacía: ninguna pieza la usa`);
  }

  private checkBuilding(path: string, spec: BuildingMinigame): void {
    this.checkIdsUnicos(`${path}/piezas`, spec.piezas.map((pz) => pz.id));
    const byId = new Map(spec.piezas.map((pz) => [pz.id, pz]));
    const suma = (ids: string[]): number => ids.reduce((total, id) => total + (byId.get(id)?.value ?? 0), 0);

    const ajenas = spec.solucion.filter((id) => !byId.has(id));
    if (ajenas.length > 0) this.fail(`${path}/solucion`, `piezas inexistentes: ${ajenas.join(', ')}`);
    this.checkSinDuplicados(`${path}/solucion`, spec.solucion, 'pieza');

    if (suma(spec.solucion) !== spec.objetivo) {
      this.fail(`${path}/solucion`, `suma ${suma(spec.solucion)} pero el objetivo es ${spec.objetivo}`);
      return;
    }
    if (spec.piezas.length > MAX_SUBSET_PIECES) return;

    const objetivo = spec.objetivo;
    const esperada = [...spec.solucion].sort().join('|');
    for (let mask = 1; mask < 1 << spec.piezas.length; mask++) {
      const combo: string[] = [];
      for (let i = 0; i < spec.piezas.length; i++) if (mask & (1 << i)) combo.push(spec.piezas[i].id);
      if ([...combo].sort().join('|') === esperada) continue;
      if (suma(combo) === objetivo) {
        this.fail(
          `${path}/objetivo`,
          `otra combinación también suma ${objetivo}: ${combo.map((id) => byId.get(id)?.label).join(' + ')}. ` +
            'El alumno podría responder bien y recibir un «incorrecto»'
        );
        return;
      }
    }
  }

  /** Batallas: cada fase se resuelve con un reto declarado y existente. */
  private checkBattles(): void {
    for (const [bid, battle] of Object.entries(this.game.battles ?? {})) {
      battle.phases.forEach((phase, index) => {
        const pp = `battles/${bid}/phases/${index}`;
        if (phase.challenge !== undefined && !(phase.challenge in (battle.challenges ?? {})))
          this.fail(`${pp}/challenge`, `Desafío inexistente en la batalla: "${phase.challenge}"`);
        this.checkRef(`${pp}/minigame`, phase.minigame, this.minigames, 'Minijuego');
        this.checkActions(phase.actions, `${pp}/actions`);
      });
      for (const [cid, ch] of Object.entries(battle.challenges ?? {})) {
        this.checkChallengeReveal(bid, cid, ch);
      }
      this.checkActions(battle.victoryActions, `battles/${bid}/victoryActions`);
    }
  }

  /**
   * Un reto no debe regalarse: ni las pistas ni el ejemplo pueden contener la
   * respuesta, y el ejemplo debe enseñar el método con OTROS números en lugar de
   * resolver la misma expresión. También valida las opciones de respuesta.
   */
  private checkChallengeReveal(bid: string, cid: string, ch: MathChallenge): void {
    const base = `battles/${bid}/challenges/${cid}`;
    if (ch.options?.length) {
      if (ch.answerIndex === undefined || ch.answerIndex < 0 || ch.answerIndex >= ch.options.length)
        this.fail(`${base}/answerIndex`, `answerIndex ausente o fuera de rango entre ${ch.options.length} opciones`);
    }
    const strip = (s: string) => s.replace(/\s+/g, '');
    const ansStr = String(ch.answer);
    const checkAnswer = ansStr.length >= 2;

    (ch.hints ?? []).forEach((hint, i) => {
      if (checkAnswer && strip(hint).includes(ansStr))
        this.fail(`${base}/hints/${i}`, `La pista revela la respuesta (${ansStr}); debe guiar el método sin dar el resultado`);
    });

    const exprNorm = strip(ch.expression);
    (ch.demoSteps ?? []).forEach((step, i) => {
      const t = strip(step.text);
      if (checkAnswer && t.includes(ansStr))
        this.fail(`${base}/demoSteps/${i}`, `El ejemplo revela la respuesta del reto (${ansStr}); debe usar otros números`);
      if (exprNorm.length >= 3 && t.includes(exprNorm))
        this.fail(`${base}/demoSteps/${i}`, `El ejemplo resuelve la misma expresión del reto; debe enseñar el método con otros números`);
    });
  }

  /** Grafo de mapas: portales conectados, spawns de entrada junto a la puerta de regreso. */
  private checkMaps(): void {
    const maps = this.game.maps ?? {};
    const start = this.game.exploration?.startMap;
    if (!start || !maps[start]) return;

    const seen = new Set<string>([start]);
    const queue = [start];
    while (queue.length) {
      const id = queue.shift() as string;
      for (const [pid, portal] of Object.entries(maps[id].portals ?? {})) {
        const pp = `maps/${id}/portals/${pid}`;
        const target = maps[portal.targetMap];
        if (!target) {
          this.fail(`${pp}/targetMap`, `Mapa destino inexistente: "${portal.targetMap}"`);
          continue;
        }
        const spawn = portal.targetSpawn ? target.spawnPoints?.[portal.targetSpawn] : undefined;
        if (portal.targetSpawn && !spawn)
          this.fail(`${pp}/targetSpawn`, `Spawn inexistente en "${portal.targetMap}": "${portal.targetSpawn}"`);
        const back = Object.values(target.portals ?? {}).find(p => p.targetMap === id);
        if (!back) {
          this.fail(`${pp}/targetMap`, `El mapa "${portal.targetMap}" no tiene portal de regreso a "${id}"`);
        } else if (spawn) {
          const d = Math.hypot(spawn.x - back.position.x, spawn.y - back.position.y);
          if (d > 0.12)
            this.fail(`${pp}/targetSpawn`, `El spawn "${portal.targetSpawn}" queda lejos de la puerta de regreso a "${id}" (d=${d.toFixed(2)}); el jugador aparecería en el extremo equivocado`);
        }
        if (!seen.has(portal.targetMap)) {
          seen.add(portal.targetMap);
          queue.push(portal.targetMap);
        }
      }
    }
    for (const id of Object.keys(maps))
      if (!seen.has(id)) this.fail(`maps/${id}`, 'Mapa inalcanzable desde el mapa inicial');
  }

  /**
   * Interior de los mapas: mismo nivel de exigencia que los escenarios.
   *
   * En modo exploración los mapas sustituyen a los escenarios, así que sus
   * objetos, interacciones y NPCs tienen que referenciarse igual de bien; si no
   * se comprueban aquí, una condición rota deja el juego sin salida y no hay
   * error que lo delate.
   */
  private checkMapContent(): void {
    for (const [mid, map] of Object.entries(this.game.maps ?? {})) {
      if (map.id !== mid) this.fail(`maps/${mid}/id`, `El id "${map.id}" no coincide con la clave "${mid}"`);
      this.checkAsset(`maps/${mid}/background`, map.background, 'image');
      this.checkAsset(`maps/${mid}/ambient`, map.ambient, 'audio');
      this.checkActions(map.onEnter, `maps/${mid}/onEnter`, map);

      for (const [pid, portal] of Object.entries(map.portals ?? {}))
        this.checkCondition(portal.visibleIf, `maps/${mid}/portals/${pid}/visibleIf`);

      for (const [nid, npc] of Object.entries(map.npcs ?? {}))
        this.checkRef(`maps/${mid}/npcs/${nid}/character`, npc.character, this.characters, 'Personaje');

      for (const [oid, obj] of Object.entries(map.objects ?? {})) {
        const op = `maps/${mid}/objects/${oid}`;
        if (obj.id !== oid) this.fail(`${op}/id`, `El id "${obj.id}" no coincide con la clave "${oid}"`);
        if (!(obj.initialState in obj.states))
          this.fail(`${op}/initialState`, `Estado inicial inexistente en "${oid}": "${obj.initialState}"`);
        for (const [stName, st] of Object.entries(obj.states))
          this.checkAsset(`${op}/states/${stName}/image`, st.image, 'image');
        this.checkCondition(obj.visibleIf, `${op}/visibleIf`);
      }

      const seenIds = new Set<string>();
      for (const [iid, inter] of Object.entries(map.interactions ?? {})) {
        const ip = `maps/${mid}/interactions/${iid}`;
        if (inter.id !== iid) this.fail(`${ip}/id`, `El id "${inter.id}" no coincide con la clave "${iid}"`);
        if (seenIds.has(inter.id)) this.fail(`${ip}/id`, `id de interacción duplicado: "${inter.id}"`);
        seenIds.add(inter.id);
        this.checkAsset(`${ip}/image`, inter.image, 'image');
        this.checkCondition(inter.visibleIf, `${ip}/visibleIf`);
        this.checkCondition(inter.enabledIf, `${ip}/enabledIf`);
        this.checkActions(inter.actions, `${ip}/actions`, map);
      }
    }
  }

  /**
   * Metas alcanzables: todo objetivo debe poder completarse y todo desenlace
   * debe poder provocarse. Un objetivo que ninguna acción cierra queda pendiente
   * para siempre, y un final inalcanzable es un juego que nunca termina.
   *
   * El recorrido es genérico a propósito: las acciones viven en escenarios,
   * mapas, diálogos, batallas y minijuegos, y un inventario manual de
   * contenedores se quedaría corto en cuanto se añada uno nuevo.
   */
  private checkReachableGoals(): void {
    const completados = new Set<string>();
    const terminados = new Set<string>();
    this.walkActions(this.game, (action) => {
      if (action.type === 'completeObjective' && typeof action.objective === 'string')
        completados.add(action.objective);
      if (action.type === 'finishGame' && typeof action.ending === 'string')
        terminados.add(action.ending);
    });
    for (const rule of this.game.conclusion?.rules ?? []) terminados.add(rule.ending);
    if (this.game.conclusion?.fallbackEnding) terminados.add(this.game.conclusion.fallbackEnding);

    for (const id of Object.keys(this.game.objectives)) {
      if (!completados.has(id))
        this.fail(`objectives/${id}`, 'Ninguna acción completa este objetivo: quedará pendiente para siempre');
    }
    for (const id of Object.keys(this.game.endings)) {
      if (!terminados.has(id))
        this.fail(`endings/${id}`, 'Ningún finishGame ni regla de conclusión lleva a este desenlace: es inalcanzable');
    }
    if (terminados.size === 0)
      this.fail('endings', 'El juego no tiene salida: ninguna acción termina la partida');
  }

  /** Recorre cualquier nodo del JSON invocando `visit` en cada acción declarativa. */
  private walkActions(node: unknown, visit: (action: Record<string, unknown>) => void): void {
    if (Array.isArray(node)) {
      for (const item of node) this.walkActions(item, visit);
      return;
    }
    if (!node || typeof node !== 'object') return;
    const obj = node as Record<string, unknown>;
    if (typeof obj.type === 'string') visit(obj);
    for (const value of Object.values(obj)) this.walkActions(value, visit);
  }

  run(): ValidationIssue[] {
    const g = this.game;

    // metadata
    if (g.mode === 'exploration') {
      const maps = new Set(Object.keys(g.maps ?? {}));
      this.checkRef('metadata/startScene', g.metadata.startScene, maps, 'Mapa inicial');
      this.checkRef('exploration/startMap', g.exploration?.startMap, maps, 'Mapa inicial');
      this.checkMaps();
    } else {
      this.checkRef('metadata/startScene', g.metadata.startScene, this.scenes, 'Escenario inicial');
    }
    this.checkRef('metadata/introDialogue', g.metadata.introDialogue, this.dialogues, 'Diálogo');

    // initialState
    (g.initialState.items ?? []).forEach((it, i) =>
      this.checkRef(`initialState/items/${i}`, it, this.items, 'Item')
    );
    (g.initialState.evidence ?? []).forEach((ev, i) =>
      this.checkRef(`initialState/evidence/${i}`, ev, this.evidence, 'Evidencia')
    );

    // characters
    for (const [cid, ch] of Object.entries(g.characters)) {
      this.checkAsset(`characters/${cid}/portrait`, ch.portrait, 'image');
      this.checkAsset(`characters/${cid}/sprite`, ch.sprite, 'image');
      if (ch.id !== cid) this.fail(`characters/${cid}/id`, `El id "${ch.id}" no coincide con la clave "${cid}"`);
    }

    // items / evidence / endings / objectives
    for (const [iid, it] of Object.entries(g.items)) {
      this.checkAsset(`items/${iid}/icon`, it.icon, 'image');
      if (it.id !== iid) this.fail(`items/${iid}/id`, `El id "${it.id}" no coincide con la clave "${iid}"`);
    }
    for (const [eid, ev] of Object.entries(g.evidence)) {
      this.checkAsset(`evidence/${eid}/icon`, ev.icon, 'image');
      if (ev.id !== eid) this.fail(`evidence/${eid}/id`, `El id "${ev.id}" no coincide con la clave "${eid}"`);
    }
    for (const [oid, ob] of Object.entries(g.objectives)) {
      if (ob.id !== oid) this.fail(`objectives/${oid}/id`, `El id "${ob.id}" no coincide con la clave "${oid}"`);
    }
    for (const [enid, en] of Object.entries(g.endings)) {
      this.checkAsset(`endings/${enid}/image`, en.image, 'image');
      if (en.id !== enid) this.fail(`endings/${enid}/id`, `El id "${en.id}" no coincide con la clave "${enid}"`);
    }

    // scenes
    for (const [sid, scene] of Object.entries(g.scenes)) {
      if (scene.id !== sid) this.fail(`scenes/${sid}/id`, `El id "${scene.id}" no coincide con la clave "${sid}"`);
      this.checkAsset(`scenes/${sid}/background`, scene.background, 'image');
      this.checkAsset(`scenes/${sid}/ambient`, scene.ambient, 'audio');
      this.checkActions(scene.onEnter, `scenes/${sid}/onEnter`, scene);
      const seenIds = new Set<string>();
      scene.interactions.forEach((inter, idx) => {
        const ip = `scenes/${sid}/interactions/${idx}`;
        if (seenIds.has(inter.id)) this.fail(`${ip}/id`, `id de interacción duplicado: "${inter.id}"`);
        seenIds.add(inter.id);
        this.checkAsset(`${ip}/image`, inter.image, 'image');
        this.checkCondition(inter.visibleIf, `${ip}/visibleIf`);
        this.checkCondition(inter.enabledIf, `${ip}/enabledIf`);
        this.checkActions(inter.actions, `${ip}/actions`, scene);
      });
      (scene.exits ?? []).forEach((ex, idx) => {
        const ep = `scenes/${sid}/exits/${idx}`;
        this.checkRef(`${ep}/to`, ex.to, this.scenes, 'Escenario destino');
        this.checkCondition(ex.visibleIf, `${ep}/visibleIf`);
      });
      for (const [oid, obj] of Object.entries(scene.objects ?? {})) {
        if (obj.id !== oid) this.fail(`scenes/${sid}/objects/${oid}/id`, `El id "${obj.id}" no coincide con la clave "${oid}"`);
        for (const [stName, st] of Object.entries(obj.states)) {
          this.checkAsset(`scenes/${sid}/objects/${oid}/states/${stName}/image`, st.image, 'image');
        }
      }
    }

    // dialogues
    for (const [did, dlg] of Object.entries(g.dialogues)) {
      if (dlg.id !== did) this.fail(`dialogues/${did}/id`, `El id "${dlg.id}" no coincide con la clave "${did}"`);
      const nodeIds = new Set(Object.keys(dlg.nodes));
      if (!nodeIds.has(dlg.startNode))
        this.fail(`dialogues/${did}/startNode`, `Nodo inicial inexistente: "${dlg.startNode}"`);
      for (const [nid, node] of Object.entries(dlg.nodes)) {
        const np = `dialogues/${did}/nodes/${nid}`;
        if (node.speaker !== 'narrator' && !this.characters.has(node.speaker))
          this.fail(`${np}/speaker`, `Personaje inexistente: "${node.speaker}"`);
        if (node.next && !nodeIds.has(node.next))
          this.fail(`${np}/next`, `Nodo destino inexistente: "${node.next}"`);
        this.checkActions(node.actions, `${np}/actions`);
        const choiceIds = new Set<string>();
        (node.choices ?? []).forEach((ch, ci) => {
          const cp = `${np}/choices/${ci}`;
          if (choiceIds.has(ch.id)) this.fail(`${cp}/id`, `id de opción duplicado: "${ch.id}"`);
          choiceIds.add(ch.id);
          if (ch.next && !nodeIds.has(ch.next))
            this.fail(`${cp}/next`, `Nodo destino inexistente: "${ch.next}"`);
          this.checkCondition(ch.visibleIf, `${cp}/visibleIf`);
          this.checkCondition(ch.enabledIf, `${cp}/enabledIf`);
          this.checkActions(ch.actions, `${cp}/actions`);
        });
      }
    }

    // conclusion
    if (g.conclusion) {
      g.conclusion.rules.forEach((rule, ri) => {
        this.checkRef(`conclusion/rules/${ri}/ending`, rule.ending, this.endings, 'Desenlace');
        this.checkCondition(rule.when, `conclusion/rules/${ri}/when`);
      });
      this.checkRef('conclusion/fallbackEnding', g.conclusion.fallbackEnding, this.endings, 'Desenlace');
    }

    // minijuegos y batallas
    this.checkMinigames();
    this.checkBattles();

    // mapas e invariantes de jugabilidad
    this.checkMapContent();
    this.checkReachableGoals();

    return this.issues;
  }
}

/**
 * Validación completa: esquema + referencias entre entidades.
 *
 * Un JSON puede ser correcto según el esquema y aun así apuntar a una
 * conversación, escenario o recurso inexistente; por eso se verifican también
 * todas las referencias por ID y los recursos declarados en `assets`.
 */
export function validateGame(data: unknown): ValidationResult {
  const schemaErrors = validateSchema(data);
  if (schemaErrors.length > 0) {
    return { valid: false, schemaErrors, referenceErrors: [] };
  }
  const game = data as GameDefinition;
  const referenceErrors = new RefChecker(game).run();
  return { valid: referenceErrors.length === 0, schemaErrors: [], referenceErrors };
}

/** Formatea los problemas en texto legible con su ubicación. */
export function formatIssues(result: ValidationResult): string {
  const lines: string[] = [];
  for (const e of result.schemaErrors) lines.push(`[esquema] ${e.path}: ${e.message}`);
  for (const e of result.referenceErrors) lines.push(`[referencia] ${e.path}: ${e.message}`);
  return lines.join('\n');
}
